import { CheckCircleOutlined, CloseCircleOutlined, FireOutlined, MinusCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { Alert, Button, Flex, Space, Tag, Card, Typography, Tabs } from "antd";
import { KeyValueItem, ListDataModel, Lists, Screen } from "feature";
import { EarningList } from "../../../earnings/ui";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useShowConfirm, PositionInfo, PositionService, OrderDto, useShowPopup, ScenarioService, ScenarioDto, SessionService, SessionDto, HedgeAuditService, HedgeAuditReport, HedgeAuditIssue } from "shared";
import { DashboardWidget } from "widgets/dashboard";
import { PositionList } from "pages/mobile/positions/ui";
const { Text, Title } = Typography;

export const ScenarioView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const showConfirm = useShowConfirm();
  const showPopUp = useShowPopup();

  const [loading, setLoading] = useState<boolean>(true);
  const [scenario, setScenario] = useState<ScenarioDto>();
  const [sessions, setSessions] = useState<SessionDto[]>();
  const [auditLoading, setAuditLoading] = useState<boolean>(false);
  const [audit, setAudit] = useState<HedgeAuditReport>();
  useEffect(() => {
    const scenarioRequest = ScenarioService.getApiScenario1({ id: Number.parseInt(id!) }).then(
      (result) => {
        setScenario(result?.data);
      }
    );
    const sessionsRequest = SessionService.getApiSession({ scenarioId: Number.parseInt(id!), activeOnly: true }).then(
      (result) => {
        setSessions(result?.data ?? []);
      }
    );
    Promise.all([scenarioRequest, sessionsRequest]).then((results) => {
      setLoading(false);
    })

  }, [id]);

  const onDelete = () => {
    if (scenario?.isActive === false) return;
    showConfirm("Остановить сценарий?", async (isOk) => {
      if (isOk) {
        setLoading(true)
        try {
          const response = await ScenarioService.postApiScenarioStop({
            scenarioId: Number.parseInt(id!),
          })
          if (response && !response?.isSuccess) {
            return showPopUp({
              title: "Ошибка",
              message: response?.errorText ?? "",
            });
          }
          else {
            navigate(-1);
          }
        }
        catch (error: any) {
          if (error) {
            return showPopUp({ title: "Ошибка", message: error.message });
          }
        }
        finally {
          setLoading(false)
        }

      }
    });
  };
  const onOpen = () => {
    if (scenario?.isActive === true) return;
    showConfirm("Запустить сценарий?", async (isOk) => {
      if (isOk) {

        setLoading(true)
        try {
          const response = await ScenarioService.postApiScenarioStart({
            scenarioId: Number.parseInt(id!),
          });
          if (response && !response?.isSuccess) {
            return showPopUp({
              title: "Ошибка",
              message: response?.errorText ?? "",
            });
          }
          else {
            navigate(-1);
          }
        }
        catch (error: any) {
          if (error) {
            return showPopUp({ title: "Ошибка", message: error.message });
          }
        }
        finally {
          setLoading(false)
        }
      }
    });
  };

  const dropError = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    showConfirm("Сбросить ошибку?", (isOk) => {
      if (isOk) {
        PositionService.deleteApiPositionError({
          positionId: Number.parseInt(id!),
        }).then((result) => {
          navigate(0);
        });
      }
    });
  };

  const onEdit = () => {
    navigate(`/scenarios/edit/${id}`, { state: scenario });
  };

  const loadHedgeAudit = async (applyFixes: boolean) => {
    const sessionId = sessions?.[0]?.id;
    if (!sessionId) {
      return showPopUp({ title: "Audit", message: "No active session found." });
    }
    setAuditLoading(true);
    try {
      const response = applyFixes
        ? await HedgeAuditService.postApiHedgeAuditSessionFixes({ sessionId })
        : await HedgeAuditService.getApiHedgeAuditSession({ sessionId });
      if (response && !response.isSuccess) {
        return showPopUp({ title: "Audit error", message: response.errorText ?? "" });
      }
      setAudit(response?.data ?? undefined);
    }
    catch (error: any) {
      return showPopUp({ title: "Audit error", message: error?.message ?? String(error) });
    }
    finally {
      setAuditLoading(false);
    }
  };

  const runHedgeAudit = async () => {
    await loadHedgeAudit(false);
  };

  const runHedgeAuditWithFixes = () => {
    showConfirm("Apply safe hedge audit fixes?", async (isOk) => {
      if (isOk) {
        await loadHedgeAudit(true);
      }
    });
  };

  const money = (value?: number | null) => (value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 6 });

  const issueColor = (issue: HedgeAuditIssue) => {
    if (issue.severity === "Error") return "red";
    if (issue.severity === "Warning") return "orange";
    return "blue";
  };

  const Status = () => {
    return <Space size={[0, 8]} wrap>
      {scenario?.isActive ?
        (<Flex>
          <Tag icon={<CheckCircleOutlined />} color="success">В работе</Tag>
          {sessions && sessions.length > 0 && <Text>{sessions[0].status}</Text>}
        </Flex>
        ) :
        (
          <Tag icon={<MinusCircleOutlined />} color="default">Остановлен</Tag>
        )}

      {/*scenario?.isError && <Tag icon={<CloseCircleOutlined />} color="error" closeIcon onClose={dropError}>Ошибка</Tag>*/}
    </Space>;
  }

  const Order = (d: OrderDto): ListDataModel => {
    return {
      key: d.id!,
      title: <div>
        <Text type={d.positionSide === "Long" ? "success" : "danger"} >
          {`${(d.positionSide === "Long" && d.orderSide === "Buy") || (d.positionSide === "Short" && d.orderSide === "Sell") ? "Open" : "Close"} ${d.positionSide}`}
        </Text> {d.isFilled && <FireOutlined twoToneColor={"orange"} />}</div>,
      description: <Flex gap={5} justify="space-between">
        <Text strong>Price: {d.price}</Text>
        <Text type="secondary" italic>{(new Date(d.createdDate!)).toLocaleString()}</Text>
      </Flex>,
      style: { borderColor: d.isPlaced ? (d.isFilled ? "#87d068" : "#2db7f5") : "gray" }
    };
  }

  const HedgeAuditPanel = () => {
    const sessionId = sessions?.[0]?.id;
    return <Flex vertical gap={10}>
      <Flex gap={8}>
        <Button
          type="primary"
          loading={auditLoading}
          disabled={!sessionId}
          onClick={runHedgeAudit}
        >
          Run hedge audit
        </Button>
        <Button
          danger
          loading={auditLoading}
          disabled={!sessionId}
          onClick={runHedgeAuditWithFixes}
        >
          Run and fix
        </Button>
      </Flex>
      {!sessionId && <Alert type="warning" showIcon message="No active session for audit." />}
      {audit && <Card size="small">
        <Flex vertical gap={8}>
          <Flex justify="space-between">
            <Text>Session</Text>
            <Text strong>{audit.sessionId}</Text>
          </Flex>
          <Flex justify="space-between">
            <Text>Expected realized PNL</Text>
            <Text strong>{money(audit.expectedRealizedPnl)}$</Text>
          </Flex>
          <Flex justify="space-between">
            <Text>Actual realized PNL</Text>
            <Text strong>{money(audit.actualRealizedPnl)}$</Text>
          </Flex>
          <Flex justify="space-between">
            <Text>Diff</Text>
            <Text strong type={(audit.realizedPnlDiff ?? 0) === 0 ? "success" : "danger"}>{money(audit.realizedPnlDiff)}$</Text>
          </Flex>
          {audit.expectedUnrealizedPnl !== undefined && audit.expectedUnrealizedPnl !== null &&
            <Flex justify="space-between">
              <Text>Expected unrealized PNL</Text>
              <Text strong>{money(audit.expectedUnrealizedPnl)}$</Text>
            </Flex>}
        </Flex>
      </Card>}
      {audit && (audit.appliedFixes?.length ?? 0) > 0 &&
        <Card size="small" title="Applied fixes">
          <Flex vertical gap={5}>
            {audit.appliedFixes?.map((fix, index) =>
              <Flex key={`${fix.type}-${fix.orderId}-${fix.earningId}-${index}`} vertical gap={2}>
                <Flex justify="space-between" gap={8}>
                  <Text strong>{fix.type}</Text>
                  {fix.newValue !== undefined && fix.newValue !== null && <Text>{money(fix.newValue)}$</Text>}
                </Flex>
                <Text>{fix.message}</Text>
                {fix.orderId && <Text code>{fix.orderId}</Text>}
                {fix.earningId && <Text type="secondary">earning #{fix.earningId}</Text>}
                {fix.oldValue !== undefined && fix.oldValue !== null &&
                  <Text type="secondary">{`old ${money(fix.oldValue)} / new ${money(fix.newValue)}`}</Text>}
              </Flex>
            )}
          </Flex>
        </Card>}
      {audit?.pairSummaries?.map((pair) =>
        <Card key={pair.pairName ?? ""} size="small" title={pair.pairName}>
          <Flex vertical gap={4}>
            <KeyValueItem label="Long units" value={money(pair.longUnits)} />
            <KeyValueItem label="Long avg" value={money(pair.longAvgPrice)} />
            <KeyValueItem label="Short units" value={money(pair.shortUnits)} />
            <KeyValueItem label="Short avg" value={money(pair.shortAvgPrice)} />
            {pair.unrealizedPnl !== undefined && pair.unrealizedPnl !== null &&
              <KeyValueItem label="Unrealized PNL" value={`${money(pair.unrealizedPnl)}$`} />}
          </Flex>
        </Card>
      )}
      {audit?.issues?.map((issue, index) =>
        <Card key={`${issue.type}-${issue.orderId}-${index}`} size="small" style={{ borderColor: issueColor(issue) }}>
          <Flex vertical gap={5}>
            <Flex justify="space-between" gap={8}>
              <Tag color={issueColor(issue)}>{issue.severity}</Tag>
              <Text strong>{issue.type}</Text>
            </Flex>
            <Text>{issue.message}</Text>
            {issue.orderId && <Text code>{issue.orderId}</Text>}
            {issue.positionId && <Text type="secondary">position #{issue.positionId}</Text>}
            {(issue.expectedValue !== undefined || issue.actualValue !== undefined) &&
              <Text>{`expected ${money(issue.expectedValue)} / actual ${money(issue.actualValue)} / diff ${money(issue.diff)}`}</Text>}
            {issue.suggestedSql && <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{issue.suggestedSql}</pre>}
          </Flex>
        </Card>
      )}
      {audit && (audit.issues?.length ?? 0) === 0 && <Alert type="success" showIcon message="No audit issues found." />}
    </Flex>;
  }

  const tabs = [
    {
      key: '1',
      label: 'Позиции в пуле',
      children: <PositionList scenarioId={scenario?.id!} activeOnly={false} mode="inline"/>,
    },
    {
      key: '2',
      label: 'Доходы/Расходы',
      children: <EarningList scenarioId={scenario?.id} />,
    },
    {
      key: '3',
      label: 'Hedge audit',
      children: <HedgeAuditPanel />,
    },
    /*
    {
      key: '2',
      label: 'Ордера',
      children: <Lists loading={false} data={scenario?.orders?.filter(o => !o.closedDate).map((d) => Order(d)) ?? []} />,
    },
    {
      key: '3',
      label: 'История',
      children: <Lists loading={false} data={scenario?.orders?.map((d) => Order(d)) ?? []} />,
    },*/
  ]

  const scenarioShortInfo = (scenario: ScenarioDto) => {
    return (
      <Flex vertical style={{ height: "100%" }}>
        <Card
          bordered
          size="small"         
          className="w-full rounded-xl"
          style={{height: "auto" }}
          >
          <Flex gap={3} vertical >
            <Flex gap={5} >
              <Text >Статус:</Text><Status />
            </Flex>
            <Flex vertical>
            {Object.keys(scenario.strategyDescription!).map((key: string) => (
                <Flex className="flex justify-between" vertical={false} key={key}>
                  <Text>{key}:</Text>
                  <Text strong style={{ color: "#beff00" }} >{scenario.strategyDescription![key]}</Text>
                </Flex>
              ))}
            </Flex>
            <Flex style={{ height: "110px", paddingBottom: "15px" }}>
              <DashboardWidget scenarioId={Number.parseInt(id!)} height="100%" />
            </Flex>
          </Flex>

        </Card>
        <Tabs defaultActiveKey="1" centered items={tabs} />
      </Flex>)
  }

  const extra = 
    (!scenario)
      ? undefined
      : { onClick: onEdit };

  return (
     <Screen loading={loading}
      btn={scenario?.isActive === false ?
        {
          type: "primary",
          title: "Запустить сценарий",
          disabled:  false,          
          onClick: onOpen,
        } : {
          type: "danger",
          title: "Остановить сценарий",
          disabled: false,
          onClick: onDelete,
        }}
      title={`${scenario?.name??""}`}
      extra={extra} >
      <div>
        {scenario && scenarioShortInfo(scenario!)}
      </div>
    </Screen>
  );
};
